import CompanyDetails from "@/components/ui/companyDetails"
import ContactInformation from "@/components/ui/contactinformation"
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import AISearchPage from "@/features/aisearch/AISearchPage"
import { selectIsAiPanelCollapsed, selectSearchQuery, selectShowResults, startSearch } from "@/features/aisearch/slice/searchslice"
import Filters from "@/features/filters/filterSection"
import { selectIsCompanyDetailsOpen } from "@/features/searchTable/slice/companyDetailsSlice"
import {
  closeContactInfo,
  selectContactInfoCompany,
  selectContactInfoContact,
  selectContactInfoFlags,
  selectIsContactInfoOpen
} from "@/features/searchTable/slice/contactInfoSlice"
import { selectSearchTableView, setView } from "@/interface/searchTable/view"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Building2, Save, Search, UsersRound } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { Outlet } from "react-router-dom"
import NavAnchor from "../utils/navAnchor"

const SearchLayout = () => {
  const dispatch = useDispatch()
  const showResults = useSelector(selectShowResults)
  const isAiPanelCollapsed = useSelector(selectIsAiPanelCollapsed)
  const isCompanyDetailsOpen = useSelector(selectIsCompanyDetailsOpen)
  const currentView = useSelector(selectSearchTableView)
  const searchQuery = useSelector(selectSearchQuery)
  const isContactInfoOpen = useSelector(selectIsContactInfoOpen)
  const contactInfoContact = useSelector(selectContactInfoContact)
  const contactInfoCompany = useSelector(selectContactInfoCompany)
  const { hideCompanyActions, hideContactFields } = useSelector(selectContactInfoFlags)

  const renderTopControls = () => (
    <div className="my-3 flex items-center justify-between gap-3">
      {/* Left side - Toggle buttons */}
      <div className="flex items-center gap-1 rounded-[6px] bg-gray-50 p-1">
        <button
          type="button"
          onClick={() => dispatch(setView("search"))}
          className={`flex items-center gap-1 rounded-[6px] px-3 py-1.5 text-xs font-medium ${
            currentView === "search" ? "bg-white text-gray-950" : "text-gray-600 hover:bg-white hover:text-gray-950"
          }`}
        >
          <Search className="size-3.5" />
          Search
        </button>
        <button
          type="button"
          onClick={() => dispatch(setView("savedFilters"))}
          className={`flex items-center gap-1 rounded-[6px] px-3 py-1.5 text-xs font-medium ${
            currentView === "savedFilters" ? "bg-white text-gray-950" : "text-gray-600 hover:bg-white hover:text-gray-950"
          }`}
        >
          <Save className="size-3.5" />
          Saved Filters
        </button>
      </div>

      {/* Center/Right side - Search and Sort */}
      <div className="flex flex-1 items-center justify-end gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => dispatch(startSearch(e.target.value))}
            className="h-9 w-[300px] bg-white pl-10 pr-3"
          />
        </div>

        {/* <Button variant="outline" className="h-9 gap-1 border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
          <SortDesc className="size-3.5" />
          Sort by
          <ChevronDown className="size-3.5" />
        </Button> */}
      </div>
    </div>
  )

  return (
    <div className="flex min-h-screen flex-col px-6">
      <div className="border-b py-5">
        <h2 className="flex flex-col text-lg font-medium">
          Discovery{" "}
          <span className="text-sm font-normal text-[#5C5C5C] dark:text-gray-400">Use AI prompts or Filters to search through our database.</span>
        </h2>
      </div>

      {isCompanyDetailsOpen ? (
        <div className="flex flex-row">
          <div className="flex-1">
            <div className="h-full p-6 pb-0 pr-0">
              <CompanyDetails />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="relative border-b bg-white dark:border-gray-800 dark:bg-background dark:from-gray-950 dark:to-gray-900/50">
            <nav className="flex space-x-3">
              <NavAnchor
                to="contacts"
                activePaths={["/app/search/contacts"]}
                activeClassName="group py-3.5 gap-2 space-x-2 border-b-2 px-1 text-sm inline-flex items-center border-primary text-primary font-semibold"
                className="group inline-flex items-center gap-2 space-x-2 border-b-2 border-transparent px-1 py-3.5 text-sm font-normal text-muted-foreground hover:text-foreground"
              >
                <UsersRound className="size-4 group-[.font-semibold]:stroke-[2.5]" /> Contact
              </NavAnchor>
              <NavAnchor
                to="companies"
                activePaths={["/app/search/companies"]}
                activeClassName="group py-3.5 gap-2 space-x-2 border-b-2 px-1 text-sm inline-flex items-center border-primary text-primary font-semibold"
                className="group inline-flex items-center gap-2 space-x-2 border-b-2 border-transparent px-1 py-3.5 text-sm font-normal text-muted-foreground hover:text-foreground"
              >
                <Building2 className="size-4 group-[.font-semibold]:stroke-[2.5]" /> Company
              </NavAnchor>
            </nav>
          </div>

          <div className="flex flex-row">
            <div className="w-80 shrink-0 overflow-y-auto">
              <Filters />
            </div>
            {showResults ? (
              <>
                <div className="flex-1">
                  <div className="p-6 pb-0 pr-0">
                    <Outlet />
                  </div>
                </div>

                {!isAiPanelCollapsed && (
                  <aside className="ml-6 mt-[18px] w-96">
                    <AISearchPage />
                  </aside>
                )}
              </>
            ) : (
              <div className="ml-6 flex-1 ">
                {renderTopControls()}
                <AISearchPage />
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={isContactInfoOpen} onOpenChange={(open) => !open && dispatch(closeContactInfo())}>
        <DialogPortal>
          <DialogOverlay />

          <DialogPrimitive.Content className="fixed right-0 top-0 z-50 h-full w-[424px] border-l border-stone-200 bg-white shadow-xl focus:outline-none">
            <ContactInformation
              contact={contactInfoContact}
              company={contactInfoCompany}
              onClose={() => dispatch(closeContactInfo())}
              hideContactFields={hideContactFields}
              hideCompanyActions={hideCompanyActions}
            />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  )
}

export default SearchLayout
